import { _isNil, _noop } from '@/littledash';

const chunkLoadErrorPattern =
  /(failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk \d+ failed|loading css chunk|unable to preload css|failed to load module script|expected a javascript module script)/i;

/**
 * Whether an error is a code-split asset that could not be loaded. Covers the Vite/Chrome,
 * Firefox and Safari wordings plus the legacy webpack forms, because a released build can be
 * reached by any of them and all mean the same thing: the hashed chunk this bundle asks for
 * is no longer deployed, so only a fresh index.html can recover.
 *
 * Two of these wordings are specific to how this application is actually served. A missing
 * asset does not 404: CloudFront maps 404 to `/index.html` with a 200 for SPA deep links, and
 * that mapping also covers `/assets/`, so a replaced chunk comes back as `200 text/html`. The
 * failure is therefore a module MIME-type rejection rather than a fetch failure, and a
 * stylesheet chunk fails through Vite's own CSS preload helper, which throws
 * `Unable to preload CSS for <url>`.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (_isNil(error)) {
    return false;
  }
  if (error instanceof Error) {
    return error.name === 'ChunkLoadError' || chunkLoadErrorPattern.test(error.message);
  }
  return typeof error === 'string' && chunkLoadErrorPattern.test(error);
};

/**
 * Pulls a document into the HTTP cache, bypassing whatever is already there.
 * Never rejects: this is opportunistic cache warming and no caller has a fallback.
 */
export const revalidateDocument = (targetUrl: string = window.location.href): Promise<void> =>
  fetch(targetUrl, { cache: 'reload' }).then(_noop, _noop);

/**
 * Boots the currently deployed release. The document is revalidated first, because a plain
 * navigation would happily replay the cached index.html that caused the staleness in the
 * first place. Navigation proceeds even if that revalidation fails — a broken page is worse
 * than an unverified reload. A different target is reached with `replace` so no history
 * entry is left behind that could walk the user back into a spent login handshake.
 */
export const reloadForRelease = async (targetUrl: string = window.location.href): Promise<void> => {
  await revalidateDocument(targetUrl);
  if (targetUrl === window.location.href) {
    window.location.reload();
  } else {
    window.location.replace(targetUrl);
  }
};
